import { useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { Grid, useTheme, CircularProgress, Button } from '@mui/material'
import { BsCheck } from 'react-icons/bs'
import { client } from '../../utils/client'
import Bugsnag from '@bugsnag/js'

type SignatureSnapshot = {
  bi_am_signed_at?: string | null
  bi_pe_signed_at?: string | null
}

const DocusignSuccess = () => {
  const theme = useTheme()
  const { global_id: globalId } = useParams<{ global_id: string }>()
  const [searchParams] = useSearchParams()
  const [isLoading, setIsLoading] = useState(true)
  const [signed, setSigned] = useState<boolean | null>(null)
  const [retry, setRetry] = useState(3000)
  const initialSnapshot = useRef<SignatureSnapshot | null>(null)
  const signerRef = useRef<'am' | 'parent' | null>(null)

  const notifyParentFrame = () => {
    if (window.parent !== window) {
      window.parent.postMessage('DOCUSIGN_SIGNED_REFRESH', window.location.origin)
    }
  }

  const markSigned = () => {
    sessionStorage.removeItem('iperia_signer')
    sessionStorage.removeItem('iperia_parent_token')
    setSigned(true)
    notifyParentFrame()
  }

  const resolveSigner = (data: SignatureSnapshot): 'am' | 'parent' => {
    if (signerRef.current) return signerRef.current

    const sessionSigner = sessionStorage.getItem('iperia_signer')
    if (sessionSigner === 'parent' || sessionSigner === 'am') {
      signerRef.current = sessionSigner
      return sessionSigner
    }

    // Session lost inside the Edusign iframe: if AM was already signed on first paint,
    // this redirect belongs to the parent employer (AM signs first by design).
    if (!initialSnapshot.current) {
      initialSnapshot.current = {
        bi_am_signed_at: data.bi_am_signed_at,
        bi_pe_signed_at: data.bi_pe_signed_at,
      }
    }
    if (
      window.parent !== window
      && initialSnapshot.current.bi_am_signed_at
      && !initialSnapshot.current.bi_pe_signed_at
    ) {
      signerRef.current = 'parent'
      return 'parent'
    }

    const querySigner = searchParams.get('signer')
    signerRef.current = querySigner === 'parent' ? 'parent' : 'am'
    return signerRef.current
  }

  const checkSignature = async (force = false) => {
    try {
      const parentToken = sessionStorage.getItem('iperia_parent_token')
      const sessionSigner = sessionStorage.getItem('iperia_signer')

      if (sessionSigner === 'parent' && parentToken) {
        const resp = await client.get('/registration/iperia/parent_status/', {
          params: { token: parentToken },
        })
        const data = resp.data as { bi_pe_signed_at?: string | null }
        if (data.bi_pe_signed_at) {
          markSigned()
          return
        }
      } else if (globalId) {
        const resp = await client.get(`/registration/${globalId}/iperia/status/`, {
          params: force ? { force: '1' } : undefined,
        })
        const data = resp.data as SignatureSnapshot
        const signer = resolveSigner(data)
        const signatureRecorded = signer === 'parent' ? data.bi_pe_signed_at : data.bi_am_signed_at
        if (signatureRecorded) {
          markSigned()
          return
        }
      } else {
        return
      }

      notifyParentFrame()
      if (retry < 20000) {
        setTimeout(() => setRetry((prevState) => prevState + prevState), retry)
      } else {
        setSigned(false)
      }
    } catch (err) {
      Bugsnag.notify(err as Error)
      console.error(err)
      notifyParentFrame()
      if (retry < 20000) {
        setTimeout(() => setRetry((prevState) => prevState + prevState), retry)
      } else {
        setSigned(false)
      }
    }
  }

  useEffect(() => {
    if (globalId || sessionStorage.getItem('iperia_parent_token')) {
      checkSignature(retry === 3000).catch((err: Error) => {
        Bugsnag.notify(err)
        console.error(err)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalId, retry])

  useEffect(() => {
    if (signed !== null) {
      setIsLoading(false)
    }
  }, [signed])

  if (isLoading) {
    return (
      <Grid
        container
        direction="column"
        justifyContent="center"
        alignItems="center"
        style={{
          display: 'flex',
          height: '100vh',
        }}
      >
        <Grid
          style={{
            flex: 1,
            height: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            display: 'flex',
          }}
        >
          <CircularProgress />
        </Grid>
      </Grid>
    )
  }

  return (
    <Grid
      container
      direction="column"
      justifyContent="center"
      alignItems="center"
      style={{
        display: 'flex',
        height: '100vh',
      }}
    >
      {!signed ? (
        <>
          <div style={{ textAlign: 'center', marginBottom: '10px' }}>
            Un problème est survenu lors de la signature, veuillez cliquer ci-dessous.
          </div>
          <Button
            variant="contained"
            sx={{ marginTop: '5px' }}
            onClick={() => {
              checkSignature(true).catch(console.error)
            }}
          >
            Rafraîchir
          </Button>
        </>
      ) : (
        <>
          <Grid
            item
            sx={{
              background: theme.palette.success.main,
              height: 80,
              width: 80,
              borderRadius: 40,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: '10px',
            }}
          >
            <BsCheck size="40px" style={{ color: 'white' }} />
          </Grid>
          Document signé avec succès
        </>
      )}
    </Grid>
  )
}

export default DocusignSuccess
